import request from "../../utils/request";
import { Result, Company } from "../../models/general";

/* ───────────────────────── Types ───────────────────────── */

export type SubscriptionTier = "Free" | "Trial" | "Paid";
export type SubscriptionStatus = "Active" | "TrialExpired" | "PaymentOverdue";

export interface CompanySubscriptionView {
  id: number;
  name: string;
  imageUrl?: string;
  email?: string;
  tier: SubscriptionTier | number;
  status: SubscriptionStatus | number;
  trialStartedAt?: string;
  trialEndsAt?: string;
  trialDaysRemaining?: number;
  paidUntil?: string;
  paidDaysRemaining?: number;
  userCount: number;
  subscriptionUpdatedAt?: string;
  subscriptionNotes?: string;
  referenceSlug?: string | null;
  referenceName?: string | null;
  referenceRaw?: string | null;
  createdOn: string;
}

export interface SignupReferenceView {
  id: number;
  slug: string;
  name: string;
  description?: string | null;
  isActive: boolean;
  companyCount: number;
  signupUrl: string;
  createdOn: string;
}

export interface SignupReferenceInput {
  slug: string;
  name: string;
  description?: string | null;
  isActive: boolean;
}

export interface BulkOperationResultView {
  succeeded: number[];
  failed: { companyId: number; reason: string }[];
}

export interface BulkMarkPaidInput {
  companyIds: number[];
  paidUntil: string | null;
  notes: string | null;
}

export interface BulkExtendTrialInput {
  companyIds: number[];
  additionalDays: number;
  notes: string | null;
}

export interface BulkRevokeInput {
  companyIds: number[];
  notes: string;
}

export interface SubscriptionAuditLogView {
  id: number;
  action: string;
  fromTier: SubscriptionTier | number;
  toTier: SubscriptionTier | number;
  fromStatus: SubscriptionStatus | number;
  toStatus: SubscriptionStatus | number;
  trialEndsAtBefore?: string;
  trialEndsAtAfter?: string;
  paidUntilBefore?: string;
  paidUntilAfter?: string;
  notes?: string;
  performedByUserId?: string;
  performedByName?: string;
  performedAt: string;
}

export interface CompanySubscriptionDetailView extends CompanySubscriptionView {
  history: SubscriptionAuditLogView[];
}

/**
 * Caller-scoped subscription state used by the global Free/Trial banner.
 * Mirrors `Mowazaf.Server.Data.General.ViewModels.MySubscriptionView`. Backed by
 * GET /api/Subscription/me which is open to any authenticated user.
 */
export interface MySubscriptionView {
  companyId?: number | null;
  tier: SubscriptionTier | number;
  status: SubscriptionStatus | number;
  trialStartedAt?: string;
  trialEndsAt?: string;
  trialDaysRemaining?: number;
  paidUntil?: string;
  paidDaysRemaining?: number;
  /** True when the caller has the system-wide Operator role — banner suppresses. */
  isOperator: boolean;
  /** True only when the company is Free AND the caller can act on it (Hr/Admin). */
  canStartTrial: boolean;
}

export interface CompanySubscriptionListResponse {
  items: CompanySubscriptionView[];
  totalCount: number;
  page: number;
  pageSize: number;
}

/**
 * Aggregate counts for the Subscription Console KPI cards. Mirrors the backend
 * `SubscriptionStatsView`. Stable across pagination — reflects every company.
 */
export interface SubscriptionStatsView {
  totalCompanies: number;
  freeCount: number;
  trialCount: number;
  paidCount: number;
  trialAvgDaysRemaining: number;
  paidExpiringSoonCount: number;
  paidOverdueCount: number;
  trialExpiredCount: number;
  pendingUpgradeRequestsCount: number;
}

export interface SubscriptionListQuery {
  page?: number;
  pageSize?: number;
  search?: string;
  tier?: SubscriptionTier | "";
  status?: SubscriptionStatus | "";
  sortBy?: string;
  sortDir?: "asc" | "desc";
}

export interface MarkPaidInput {
  paidUntil: string | null;
  notes: string | null;
}

export interface ExtendTrialOpInput {
  additionalDays: number;
  notes: string | null;
}

export interface StartTrialInput {
  days: number | null;
  notes: string | null;
}

export interface RevokeInput {
  notes: string;
}

/* ────────────── Enum coercion helpers (numbers OR strings) ────────────── */

const TIER_NAMES: SubscriptionTier[] = ["Free", "Trial", "Paid"];
const STATUS_NAMES: SubscriptionStatus[] = ["Active", "TrialExpired", "PaymentOverdue"];

export function coerceTier(value: SubscriptionTier | number | undefined | null): SubscriptionTier {
  if (typeof value === "number") return TIER_NAMES[value] ?? "Free";
  if (typeof value === "string" && (TIER_NAMES as string[]).includes(value)) return value as SubscriptionTier;
  return "Free";
}

export function coerceStatus(
  value: SubscriptionStatus | number | undefined | null,
): SubscriptionStatus {
  if (typeof value === "number") return STATUS_NAMES[value] ?? "Active";
  if (typeof value === "string" && (STATUS_NAMES as string[]).includes(value)) {
    return value as SubscriptionStatus;
  }
  return "Active";
}

/* ───────────────────────── Endpoints ───────────────────────── */

function toQueryString(query: SubscriptionListQuery): string {
  const params = new URLSearchParams();
  if (query.page) params.append("page", String(query.page));
  if (query.pageSize) params.append("pageSize", String(query.pageSize));
  if (query.search) params.append("search", query.search);
  if (query.tier) params.append("tier", String(query.tier));
  if (query.status) params.append("status", String(query.status));
  if (query.sortBy) params.append("sortBy", query.sortBy);
  if (query.sortDir) params.append("sortDir", query.sortDir);
  const qs = params.toString();
  return qs ? `?${qs}` : "";
}

/**
 * Caller-scoped subscription state — read on every page-load by the banner.
 * Hits TierGuard's 30s cache on the server, so it's cheap even when polled.
 */
export async function getMySubscription() {
  const response = await request.get<Result<MySubscriptionView>>("Subscription/me");
  return response.data;
}

export async function getSubscriptionCompanies(query: SubscriptionListQuery = {}) {
  const response = await request.get<Result<CompanySubscriptionListResponse>>(
    `Subscription/companies${toQueryString(query)}`,
  );
  return response.data;
}

export async function getSubscriptionStats() {
  const response = await request.get<Result<SubscriptionStatsView>>("Subscription/stats");
  return response.data;
}

export async function getSubscriptionCompany(id: number) {
  const response = await request.get<Result<CompanySubscriptionDetailView>>(
    `Subscription/companies/${id}`,
  );
  return response.data;
}

export async function getSubscriptionHistory(id: number) {
  const response = await request.get<Result<SubscriptionAuditLogView[]>>(
    `Subscription/companies/${id}/history`,
  );
  return response.data;
}

export async function markCompanyPaid(id: number, input: MarkPaidInput) {
  const response = await request.post<Result<Company>>(
    `Subscription/companies/${id}/mark-paid`,
    input,
  );
  return response.data;
}

export async function extendCompanyTrial(id: number, input: ExtendTrialOpInput) {
  const response = await request.post<Result<Company>>(
    `Subscription/companies/${id}/extend-trial`,
    input,
  );
  return response.data;
}

export async function startCompanyTrial(id: number, input: StartTrialInput) {
  const response = await request.post<Result<Company>>(
    `Subscription/companies/${id}/start-trial`,
    input,
  );
  return response.data;
}

export async function revokeCompanySubscription(id: number, input: RevokeInput) {
  const response = await request.post<Result<Company>>(
    `Subscription/companies/${id}/revoke`,
    input,
  );
  return response.data;
}

/* ───────────────────────── Phase 7 — upgrade requests ───────────────────────── */

export interface RequestUpgradeBody {
  message?: string | null;
}

export interface SubscriptionUpgradeRequestView {
  notificationId: number;
  companyId: number;
  companyName: string;
  companyImageUrl?: string | null;
  currentTier: SubscriptionTier | number;
  currentStatus: SubscriptionStatus | number;
  trialDaysRemaining?: number | null;
  userCount: number;
  requestedByUserId?: string | null;
  requestedByName?: string | null;
  message?: string | null;
  requestedAt: string;
}

/**
 * Trial/Free callers ping the Operator team. The Operator inbox + email distribution
 * receives the request. Backend rejects callers whose company is Paid.
 */
export async function requestUpgrade(body: RequestUpgradeBody) {
  const response = await request.post<Result<boolean>>("Subscription/request-upgrade", body);
  return response.data;
}

export async function getPendingUpgradeRequests() {
  const response = await request.get<Result<SubscriptionUpgradeRequestView[]>>(
    "Subscription/upgrade-requests",
  );
  return response.data;
}

export async function dismissUpgradeRequest(notificationId: number) {
  const response = await request.post<Result<boolean>>(
    `Subscription/upgrade-requests/${notificationId}/dismiss`,
    {},
  );
  return response.data;
}

/* ─────────────── Signup-reference catalog (operator-only) ─────────────── */

export async function listSignupReferences() {
  const response = await request.get<Result<SignupReferenceView[]>>("Subscription/references");
  return response.data;
}

export async function createSignupReference(input: SignupReferenceInput) {
  const response = await request.post<Result<SignupReferenceView>>(
    "Subscription/references",
    input,
  );
  return response.data;
}

export async function updateSignupReference(id: number, input: SignupReferenceInput) {
  const response = await request.put<Result<SignupReferenceView>>(
    `Subscription/references/${id}`,
    input,
  );
  return response.data;
}

export async function deleteSignupReference(id: number) {
  const response = await request.delete<Result<boolean>>(`Subscription/references/${id}`);
  return response.data;
}

/* ─────────────── Bulk operations (operator-only) ─────────────── */

export async function bulkMarkPaid(input: BulkMarkPaidInput) {
  const response = await request.post<Result<BulkOperationResultView>>(
    "Subscription/companies/bulk/mark-paid",
    input,
  );
  return response.data;
}

export async function bulkExtendTrial(input: BulkExtendTrialInput) {
  const response = await request.post<Result<BulkOperationResultView>>(
    "Subscription/companies/bulk/extend-trial",
    input,
  );
  return response.data;
}

export async function bulkRevoke(input: BulkRevokeInput) {
  const response = await request.post<Result<BulkOperationResultView>>(
    "Subscription/companies/bulk/revoke",
    input,
  );
  return response.data;
}

/* ─────────────── HR upgrade nudge (caller in their own company) ─────────────── */

export async function notifyHrToUpgrade() {
  const response = await request.post<Result<boolean>>("Subscription/notify-hr-to-upgrade", {});
  return response.data;
}
